import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useToast } from '../contexts/ToastContext';
import LoadingSpinner from '../components/LoadingSpinner';
import GoogleAuthButton from '../components/GoogleAuthButton';
import { usePageTitle } from '../hooks/usePageTitle';
import { sanitizeRedirect } from '../utils/redirect';
import { trackClick } from '../utils/analytics';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const ZapIcon = () => (
  <svg className="w-5 h-5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
  </svg>
);

const ChartIcon = () => (
  <svg className="w-5 h-5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
  </svg>
);

const TargetIcon = () => (
  <svg className="w-5 h-5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const LoginPage: React.FC = () => {
  usePageTitle('Sign In');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, loading, error, clearError } = useAuthStore();
  const { showError } = useToast();

  const redirectTo = sanitizeRedirect(searchParams.get('redirect'));

  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [ssoEnabled, setSsoEnabled] = useState(false);
  const [ssoOrgId, setSsoOrgId] = useState<string | null>(null);
  const [ssoChecking, setSsoChecking] = useState(false);
  const ssoDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const email = formData.email;
    const domain = email.split('@')[1];
    if (!domain || !domain.includes('.')) {
      setSsoEnabled(false);
      setSsoOrgId(null);
      return;
    }

    if (ssoDebounceRef.current) clearTimeout(ssoDebounceRef.current);
    ssoDebounceRef.current = setTimeout(async () => {
      setSsoChecking(true);
      try {
        const res = await fetch(`${API_BASE_URL}/auth/sso/check/?email=${encodeURIComponent(email)}`);
        if (res.ok) {
          const data = await res.json();
          setSsoEnabled(data.sso_enabled || false);
          setSsoOrgId(data.org_id || null);
        } else {
          setSsoEnabled(false);
          setSsoOrgId(null);
        }
      } catch {
        setSsoEnabled(false);
        setSsoOrgId(null);
      } finally {
        setSsoChecking(false);
      }
    }, 600);

    return () => {
      if (ssoDebounceRef.current) clearTimeout(ssoDebounceRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.email]);

  const handleSsoLogin = () => {
    if (!ssoOrgId) return;
    trackClick('LoginPage_button_sso_login');
    // Full-page navigation so SAML request state is stored server-side (fetch + cookies fail on ACS POST).
    window.location.href = `${API_BASE_URL}/auth/saml/login/?org=${encodeURIComponent(ssoOrgId)}`;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) clearError();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    trackClick('LoginPage_button_sign_in');
    try {
      await login({ email: formData.email, password: formData.password });
      navigate(redirectTo);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Login failed');
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Brand Panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-orange-500 via-orange-600 to-orange-500 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-72 h-72 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-amber-400/30 rounded-full blur-3xl" />
          <div className="absolute top-3/4 left-1/3 w-48 h-48 bg-white/10 rounded-full blur-2xl" />
        </div>

        <div className="relative z-10 flex flex-col justify-center px-12 xl:px-20">
          <Link to="/" onClick={() => trackClick('LoginPage_logo_navigate_home')} className="flex items-center gap-3 mb-12">
            <img src="/logo.jpeg" alt="Retrosive" className="w-12 h-12 rounded-xl object-contain bg-white p-0.5" />
            <span className="text-2xl font-bold text-white">Retrosive</span>
          </Link>

          <h1 className="text-4xl xl:text-5xl font-bold text-white mb-6 leading-tight">
            Welcome back to<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-300">
              better retrospectives
            </span>
          </h1>

          <p className="text-lg text-orange-50 mb-12 max-w-md">
            Continue where you left off. Your team's insights and action items are waiting.
          </p>

          <div className="space-y-4 mb-12">
            {[
              { icon: <ZapIcon />, text: 'Real-time collaboration' },
              { icon: <ChartIcon />, text: 'Actionable insights' },
              { icon: <TargetIcon />, text: 'Track your progress' },
            ].map((feature, i) => (
              <div key={i} className="flex items-center gap-3">
                {feature.icon}
                <span className="text-orange-50">{feature.text}</span>
              </div>
            ))}
          </div>

          {/* Trust badge */}
          <div className="flex items-center gap-3">
            <div className="flex -space-x-2">
              {['bg-amber-400', 'bg-orange-400', 'bg-emerald-400', 'bg-pink-400'].map((color, i) => (
                <div key={i} className={`w-8 h-8 rounded-full ${color} border-2 border-orange-600`} />
              ))}
            </div>
            <p className="text-sm text-orange-100">
              <span className="text-white font-semibold">10,000+</span> teams run better retros
            </p>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent" />
      </div>

      {/* Right Side - Login Form */}
      <div className="flex-1 flex flex-col justify-center px-6 py-12 lg:px-12 xl:px-20 bg-white">
        <div className="w-full max-w-md mx-auto">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo.jpeg" alt="Retrosive" className="w-10 h-10 rounded-xl object-contain" />
              <span className="text-xl font-bold text-slate-900">Retrosive</span>
            </Link>
          </div>

          <div className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900">Welcome back</h2>
            <p className="mt-2 text-slate-500">
              Don't have an account?{' '}
              <Link to="/register" onClick={() => trackClick('LoginPage_link_register')} className="font-medium text-orange-500 hover:text-orange-500">
                Create one free
              </Link>
            </p>
          </div>

          {/* Google Sign In — primary CTA */}
          <GoogleAuthButton
            text="Continue with Google"
            variant="light"
            redirectTo={redirectTo}
          />

          {/* Divider */}
          <div className="my-6 flex items-center">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="px-4 text-sm text-slate-400">or continue with email</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1.5">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                autoComplete="email"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                placeholder="you@example.com"
              />
            </div>

            {/* SSO Button — shown when email domain has SSO enabled */}
            {(ssoEnabled || ssoChecking) && (
              <div>
                <button
                  type="button"
                  onClick={handleSsoLogin}
                  disabled={ssoChecking || !ssoEnabled}
                  className="w-full py-3 flex items-center justify-center gap-2 bg-violet-600 hover:bg-violet-700 text-white font-semibold rounded-xl transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {ssoChecking ? (
                    <LoadingSpinner size="small" />
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                      Continue with SSO
                    </>
                  )}
                </button>
                <p className="mt-1.5 text-xs text-slate-400 text-center">
                  Your organization uses Single Sign-On
                </p>
              </div>
            )}

            {/* Password */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label htmlFor="password" className="block text-sm font-medium text-slate-700">
                  Password
                </label>
                <Link
                  to="/forgot-password"
                  onClick={() => trackClick('LoginPage_link_forgot_password')}
                  className="text-sm text-orange-500 hover:text-orange-500 font-medium"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  autoComplete="current-password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all pr-12"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => {
                    trackClick('LoginPage_password_toggle');
                    setShowPassword(!showPassword);
                  }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-xl flex items-start gap-2">
                <svg className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {loading ? <LoadingSpinner size="small" /> : 'Sign In'}
            </button>
          </form>

          <p className="mt-8 text-center text-sm text-slate-500">
            By signing in, you agree to our{' '}
            <Link to="/terms" onClick={() => trackClick('LoginPage_link_terms')} className="text-orange-500 hover:underline">Terms</Link> and{' '}
            <Link to="/privacy" onClick={() => trackClick('LoginPage_link_privacy')} className="text-orange-500 hover:underline">Privacy Policy</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
